/*!
 * Knockout LifeCycle for object instances 🥊  @tko/lifecycle@4.0.0-alpha7.4
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { addDisposeCallback, createSymbolOrString } from '@tko/utils';
import { computed } from '@tko/computed';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var SUBSCRIPTIONS = createSymbolOrString('LifeCycle Subscriptions List');
var ANCHOR_NODE = createSymbolOrString('LifeCycle Anchor Node');
var LifeCycle = /** @class */ (function () {
    function LifeCycle() {
    }
    // NOTE: For more advanced integration as an ES6 mixin, see e.g.:
    // http://justinfagnani.com/2015/12/21/real-mixins-with-javascript-classes/
    /**
     * Copy the properties of the LifeCycle class to the target (or its prototype)
     *
     * NOTE: getOwnPropertyNames is needed to copy the non-enumerable properties.
     */
    LifeCycle.mixInto = function (Constructor) {
        var e_1, _a;
        var target = Constructor.prototype || Constructor;
        var mixin = LifeCycle.prototype;
        try {
            for (var _b = __values(Object.getOwnPropertyNames(mixin)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var prop = _c.value;
                target[prop] = mixin[prop];
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b["return"])) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    LifeCycle.prototype.subscribe = function (observable, action, subscriptionType) {
        if (typeof action === 'string') {
            action = this[action];
        }
        this.addDisposable(observable.subscribe(action, this, subscriptionType));
    };
    LifeCycle.prototype.computed = function (params) {
        if (typeof params === 'string') {
            params = { read: this[params], write: this[params] };
        }
        if (typeof params === 'object') {
            params = Object.assign({ owner: this }, params);
        }
        else if (typeof params === 'function') {
            var proto = Object.getPrototypeOf(this);
            if (proto && proto[params.name] === params) {
                params = params.bind(this);
            }
            params = { read: params, write: params };
        }
        else {
            throw new Error('LifeCycle::computed not given a valid type.');
        }
        params.disposeWhenNodeIsRemoved = this[ANCHOR_NODE];
        return this.addDisposable(computed(params));
    };
    /**
     * Add an event listener for the given or anchored node.
     * @param {node} [node] (optional) The target node (otherwise the anchored node)
     * @param {string} [type] Event type
     * @param {function|string} [action] Either call the given function or `this[action]`
     * @param {object} [options] (optional) Passed as `options` to `node.addEventListener`
     */
    LifeCycle.prototype.addEventListener = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var node = args[0].nodeType ? args.shift() : this[ANCHOR_NODE];
        var _a = __read(args, 3), type = _a[0], act = _a[1], options = _a[2];
        var handler = typeof act === 'string' ? this[act].bind(this) : act;
        this.__addEventListener(node, type, handler, options);
    };
    LifeCycle.prototype.__addEventListener = function (node, eventType, handler, options) {
        node.addEventListener(eventType, handler, options);
        function dispose() { node.removeEventListener(eventType, handler); }
        addDisposeCallback(node, dispose);
        this.addDisposable({ dispose: dispose });
    };
    LifeCycle.prototype.anchorTo = function (node) {
        var _this = this;
        addDisposeCallback(node, function () { return _this.dispose(); });
        this[ANCHOR_NODE] = node;
    };
    LifeCycle.prototype.dispose = function () {
        var subscriptions = this[SUBSCRIPTIONS] || [];
        subscriptions.forEach(function (s) { return s.dispose(); });
        this[SUBSCRIPTIONS] = [];
        this[ANCHOR_NODE] = null;
    };
    LifeCycle.prototype.addDisposable = function (subscription) {
        var subscriptions = this[SUBSCRIPTIONS] || [];
        if (!this[SUBSCRIPTIONS]) {
            this[SUBSCRIPTIONS] = subscriptions;
        }
        if (typeof subscription.dispose !== 'function') {
            throw new Error('Lifecycle::addDisposable argument missing `dispose`.');
        }
        subscriptions.push(subscription);
        return subscription;
    };
    return LifeCycle;
}());

export { LifeCycle };
//# sourceMappingURL=lifecycle.js.map
